import { Injectable } from "@angular/core";
import * as CookieConsent from "vanilla-cookieconsent";

/**
 * GDPR/PIPA cookie-consent banner (vanilla-cookieconsent) for the admin portal.
 *
 * Decoupled: nothing in the app references this; wired by one APP_INITIALIZER
 * line in app.module.ts. Opt-in / deny-by-default — no non-essential storage
 * fires until the user accepts.
 *
 * Admin tracker reality (2026-06 audit):
 *  - Universal Analytics (UA-147618337-2) was RETIRED — UA is defunct (Google
 *    shut it down July 2023) and is no longer loaded from index.prod.html.
 *  - There is NO GTM container on admin, so the old `consent_update` dataLayer
 *    event gated nothing — removed.
 *  - Microsoft Clarity, New Relic Browser and Usetiful are NOT present on admin
 *    (no script tag, no GTM to inject them) — they were listed in the cookie
 *    table by mistake and have been removed (GDPR Art 13 transparency).
 *  - After retiring UA, admin loads NO analytics / product-insight trackers, so
 *    the "analytics" category has been removed entirely. Only the always-on
 *    "necessary" category remains. If a real GA4 tag is ever added, reintroduce
 *    an analytics category here and map it onto Consent Mode v2 (the
 *    default-denied `gtag('consent','default',…)` block already lives in
 *    index.prod.html as the safe baseline).
 *
 * Notes:
 *  - `hideFromBots: false`: the default (true) suppresses the modal whenever
 *    `navigator.webdriver` is set, which hides it from automated tests.
 *  - run() is deferred to window `load`: at APP_INITIALIZER the DOM isn't ready.
 *  - The small banner stays brief; the per-category detail lives in the
 *    preferences modal. A persistent "Cookie settings" entry in the left
 *    navigation re-opens the preferences modal at any time.
 */
@Injectable({ providedIn: "root" })
export class ConsentService {
  /** Called once at bootstrap. */
  init(): void {
    if (document.readyState === "complete") {
      this.runConsent();
    } else {
      window.addEventListener("load", () => this.runConsent(), { once: true });
    }
  }

  private runConsent(): void {
    CookieConsent.run({
      mode: "opt-in", // nothing non-essential fires until explicit accept
      revision: 1,
      hideFromBots: false,
      cookie: { name: "xpath_consent" },
      guiOptions: {
        consentModal: {
          layout: "box inline", // inline buttons, not stacked
          position: "bottom right",
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: { layout: "box", equalWeightButtons: true },
      },
      categories: {
        // Only strictly necessary storage is used on admin today.
        necessary: { enabled: true, readOnly: true },
      },
      language: {
        default: "en",
        translations: {
          en: {
            // Keep the banner short — details go in the preferences modal.
            consentModal: {
              title: "We value your privacy",
              description:
                "We only use cookies that are strictly necessary to keep the portal working — there are no analytics or tracking cookies. See <strong>Manage preferences</strong> for the full list.",
              acceptAllBtn: "Accept",
              showPreferencesBtn: "Manage preferences",
            },
            // All the detail lives here.
            preferencesModal: {
              title: "Manage cookie preferences",
              acceptAllBtn: "Accept",
              savePreferencesBtn: "Save my choices",
              closeIconLabel: "Close",
              sections: [
                {
                  title: "How we use cookies",
                  description:
                    "This admin portal uses only strictly necessary cookies — there are currently no analytics, advertising or third-party tracking cookies. The category below cannot be switched off because the portal cannot function without it.",
                },
                {
                  title: "Strictly necessary",
                  description:
                    "Required for the portal to function — authentication (your Keycloak sign-in session) and your cookie-consent choice itself. These cannot be switched off.",
                  linkedCategory: "necessary",
                  cookieTable: {
                    caption: "Cookies in this category",
                    headers: {
                      name: "Cookie",
                      provider: "Provider",
                      desc: "What it does",
                      exp: "Expiry",
                    },
                    body: [
                      {
                        name: "xpath_consent",
                        provider: "xpath.global (this portal)",
                        desc: "Remembers your cookie-consent choice so we don't ask again on every visit.",
                        exp: "6 months",
                      },
                      {
                        name: "Keycloak session",
                        provider: "xpath.global (Keycloak SSO)",
                        desc: "Keeps you signed in and secures your authenticated session.",
                        exp: "Session",
                      },
                    ],
                  },
                },
              ],
            },
          },
        },
      },
    });
  }
}
